/**
 * A CAS module's authored assessment against the techniques its QQI
 * component specification sets out.
 *
 * @module utils/cas-assessment-comparison
 */

import type { ModuleAssessment } from "@/types";
import type { CasAssessmentTechnique } from "@/types/cas";

export interface TechniqueComparison {
  technique: string;
  /** The specification's weighting; null when it does not list the technique. */
  specified: number | null;
  /** The module's summed weighting; null when nothing authored matches. */
  authored: number | null;
}

/**
 * The QQI technique a label names. Specifications write "Portfolio /
 * Collection of Work" and "Examination - Theory" where the editor offers
 * "Portfolio" and "Exam (Online)".
 */
function techniqueKey(label: string): string {
  const key = label.trim().toLowerCase();
  if (key.startsWith("portfolio") || key.startsWith("collection")) {
    return "portfolio";
  }
  if (key.startsWith("skills")) {
    return "skills";
  }
  if (key.includes("practical")) {
    return "practical";
  }
  return key.startsWith("exam") ? "examination" : key;
}

/** One row per specified technique, then any authored technique the specification lacks. */
export function compareAssessmentTechniques(
  specified: readonly CasAssessmentTechnique[],
  assessments: readonly ModuleAssessment[],
): TechniqueComparison[] {
  const authored = new Map<string, { label: string; weighting: number }>();
  assessments.forEach((assessment) => {
    const key = techniqueKey(assessment.type);
    const previous = authored.get(key);
    authored.set(key, {
      label: previous?.label ?? assessment.type,
      weighting:
        (previous?.weighting ?? 0) + Number(assessment.weighting ?? assessment.weight ?? 0),
    });
  });
  const rows = specified.map(({ technique, weighting }): TechniqueComparison => {
    const key = techniqueKey(technique);
    const match = authored.get(key);
    authored.delete(key);
    return { technique, specified: weighting, authored: match?.weighting ?? null };
  });
  authored.forEach(({ label, weighting }) =>
    rows.push({ technique: label, specified: null, authored: weighting }),
  );
  return rows;
}
